import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import type { Feature, GeoJsonProperties, Polygon } from 'geojson';
import { GeoJSONSource, LngLat, Map, MapGeoJSONFeature, Point as MapLibrePoint, PointLike, Source } from 'maplibre-gl';

import { MapBaseSource, MapSourceTypeEnum } from '@connect-services/client/components/map/map.component';
import { LayerStyleTypeEnum } from '@connect-services/client/models/highlight-entity.model';
import { MapBaseService } from '@connect-services/client/services/map-base.service';
import { EntityTypeEnum } from '@connect-services/client/services/map-draw.service';
import { SourcesService } from '@connect-services/client/services/sources.service';

const CIRCLE_LAYER_ID = 'click-circle-layer';
export const CUSTOM_INDICATOR_SOURCE_ID = 'custom_indicator_source_id';
const DEFAULT_MAPLIBRE_TOLERANCE_FEATURE = 6;
const SCALE_FACTOR_MULTIPLIER = 1.7;
const ZOOM_SENSITIVITY_REDUCTION = 0.5;

interface Indicator {
    center: LngLat;
    geoBbox: [LngLat, LngLat];
    zoomOnClick: number;
}

@Injectable({ providedIn: 'root' })
export class MapClickIndicatorService extends MapBaseService {
    public readonly customIndicatorSourceId: string = CUSTOM_INDICATOR_SOURCE_ID;
    public readonly indicator: WritableSignal<Indicator | null> = signal<Indicator | null>(null);

    private readonly layerColor: Signal<string> = computed(() => {
        return getComputedStyle(document.documentElement).getPropertyValue('--secondary').trim();
    });
    private readonly sourcesService = inject(SourcesService);

    public createIndicatorSourceOnClick(point: MapLibrePoint, bbox: [PointLike, PointLike]): void {
        if (!this.map) {
            return;
        }

        const topLeft: LngLat = this.map.unproject(bbox[0]);
        const bottomRight: LngLat = this.map.unproject(bbox[1]);

        this.indicator.set({
            center: this.map.unproject(point),
            geoBbox: [topLeft, bottomRight],
            zoomOnClick: this.map.getZoom(),
        });

        this.buildSquareFromBbox(this.map, [topLeft, bottomRight]);
    }

    public filterIndicatorSource(features: MapGeoJSONFeature[]): MapGeoJSONFeature[] {
        return features.filter((feature: MapGeoJSONFeature) => feature.source !== CUSTOM_INDICATOR_SOURCE_ID);
    }

    public registerZoomListener(): void {
        if (!this.map) {
            return;
        }

        this.map.on('move', (): void => {
            if (!this.isIndicatorExist()) {
                return;
            }

            const zoomFactor: number = this.getZoomCompensationFactor(
                this.indicator()!.zoomOnClick,
                this.map!.getZoom(),
            );

            const adjustedBbox: [LngLat, LngLat] = this.scaleBBox(this.indicator()!.geoBbox, zoomFactor);

            this.buildSquareFromBbox(this.map!, adjustedBbox);
        });
    }

    public removeIndicatorSource(): void {
        this.indicator.set(null);

        if (!this.isMapPresent()) {
            return;
        }

        const sourceExists: boolean = !!this.map.getSource(CUSTOM_INDICATOR_SOURCE_ID);
        const layerExists: boolean = !!this.map.getLayer(CIRCLE_LAYER_ID);

        if (!sourceExists && !layerExists) {
            return;
        }

        if (layerExists) {
            this.map.removeLayer(CIRCLE_LAYER_ID);
        }

        if (sourceExists) {
            this.map.removeSource(CUSTOM_INDICATOR_SOURCE_ID);
        }

        this.sourcesService.sources.update((sources: MapBaseSource<unknown>[]) =>
            sources.filter((source: MapBaseSource<unknown>) => source.sourceId !== CUSTOM_INDICATOR_SOURCE_ID),
        );
    }

    private addIndicatorSource(map: Map, markerFeature: Feature<Polygon, GeoJsonProperties>): void {
        const indicatorSource: Source | undefined = this.buildSource(map, markerFeature);

        if (!indicatorSource) {
            return;
        }

        const baseSource: MapBaseSource<unknown> = {
            data: markerFeature,
            isVisible: true,
            name: 'Custom Area Indicator',
            sourceId: indicatorSource.id,
            style: [],
            type: MapSourceTypeEnum.LAYER,
        };

        this.sourcesService.sources.update((sources: MapBaseSource<unknown>[]) => [...sources, baseSource]);
    }

    private buildSource(map: Map, markerFeature: Feature<Polygon, GeoJsonProperties>): Source | undefined {
        map.addSource(CUSTOM_INDICATOR_SOURCE_ID, {
            data: markerFeature,
            type: 'geojson',
        }).addLayer({
            id: CIRCLE_LAYER_ID,
            paint: {
                'fill-color': this.layerColor(),
                'fill-opacity': 0.3,
                'fill-outline-color': '#00183e',
            },
            source: CUSTOM_INDICATOR_SOURCE_ID,
            type: LayerStyleTypeEnum.FILL,
        });

        return map.getSource(CUSTOM_INDICATOR_SOURCE_ID);
    }

    private buildSquareFromBbox(map: Map, [topLeft, bottomRight]: [LngLat, LngLat]): void {
        const padding = DEFAULT_MAPLIBRE_TOLERANCE_FEATURE;

        const topLeftPx: MapLibrePoint = map.project(topLeft);
        const bottomRightPx: MapLibrePoint = map.project(bottomRight);

        const [expandedTopLeftPx, expandedBottomRightPx] = this.expandPixelBounds(topLeftPx, bottomRightPx, padding);

        const expandedTopLeft: LngLat = map.unproject(expandedTopLeftPx);
        const expandedBottomRight: LngLat = map.unproject(expandedBottomRightPx);

        const [expandedTopRight, expandedBottomLeft] = [
            new LngLat(expandedBottomRight.lng, expandedTopLeft.lat),
            new LngLat(expandedTopLeft.lng, expandedBottomRight.lat),
        ];

        const polygon: Feature<Polygon, GeoJsonProperties> = {
            geometry: {
                coordinates: [
                    [
                        [expandedTopLeft.lng, expandedTopLeft.lat],
                        [expandedTopRight.lng, expandedTopRight.lat],
                        [expandedBottomRight.lng, expandedBottomRight.lat],
                        [expandedBottomLeft.lng, expandedBottomLeft.lat],
                        [expandedTopLeft.lng, expandedTopLeft.lat],
                    ],
                ],
                type: EntityTypeEnum.POLYGON,
            },
            properties: {},
            type: 'Feature',
        };

        if (!map.getSource(CUSTOM_INDICATOR_SOURCE_ID)) {
            this.addIndicatorSource(map, polygon);

            return;
        }
        this.updateIndicatorSource(map, polygon);
    }

    private expandPixelBounds(
        topLeftPx: MapLibrePoint,
        bottomRightPx: MapLibrePoint,
        padding: number,
    ): [MapLibrePoint, MapLibrePoint] {
        return [
            new MapLibrePoint(topLeftPx.x - padding, topLeftPx.y - padding),
            new MapLibrePoint(bottomRightPx.x + padding, bottomRightPx.y + padding),
        ];
    }

    private getZoomCompensationFactor(zoomOnClick: number, currentZoom: number): number {
        const zoomDiff: number = currentZoom - zoomOnClick;

        const scaleFactor: number = zoomDiff * (1 / (1 + Math.abs(zoomDiff) * ZOOM_SENSITIVITY_REDUCTION));

        return 1 + scaleFactor * SCALE_FACTOR_MULTIPLIER;
    }

    private isIndicatorExist(): boolean {
        return !(!this.indicator?.()?.center || !this.indicator()?.zoomOnClick || !this.indicator()?.geoBbox);
    }

    private scaleBBox([topLeft, bottomRight]: [LngLat, LngLat], scale: number): [LngLat, LngLat] {
        const centerLng: number = (topLeft.lng + bottomRight.lng) / 2;
        const centerLat: number = (topLeft.lat + bottomRight.lat) / 2;

        const lngHalf: number = ((bottomRight.lng - topLeft.lng) / 2) * scale;
        const latHalf: number = ((topLeft.lat - bottomRight.lat) / 2) * scale;

        const newTopLeft: LngLat = new LngLat(centerLng - lngHalf, centerLat + latHalf);
        const newBottomRight: LngLat = new LngLat(centerLng + lngHalf, centerLat - latHalf);

        return [newTopLeft, newBottomRight];
    }

    private updateIndicatorSource(map: Map, circle: Feature<Polygon, GeoJsonProperties>): void {
        const source = map.getSource(CUSTOM_INDICATOR_SOURCE_ID) as GeoJSONSource;

        source.setData(circle);
    }
}
